import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NotifCtx = createContext(null);

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const lastIdsRef = useRef(new Set());

  const load = useCallback(async () => {
    if (!user) { setItems([]); setUnread(0); lastIdsRef.current = new Set(); return; }
    try {
      const { data } = await api.get("/notifications");
      // detect newly-arrived unread ones (not previously seen) and pop a toast
      const seen = lastIdsRef.current;
      const fresh = data.items.filter(n => !n.read && !seen.has(n.notification_id));
      if (seen.size > 0) {
        fresh.forEach(n => {
          toast(n.title, { description: n.body, action: { label: "View", onClick: () => {} } });
        });
      }
      lastIdsRef.current = new Set(data.items.map(n => n.notification_id));
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (nid) => { await api.post(`/notifications/${nid}/read`); load(); };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <NotifCtx.Provider value={{ items, unread, markRead, markAll, reload: load }}>
      {children}
    </NotifCtx.Provider>
  );
}

export function useNotifications() { return useContext(NotifCtx); }
